import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import track from 'react-tracking';
import { Collapsible, OverlayTrigger, Tooltip } from '@edx/paragon';
import {
  LibraryBooks, CalendarMonth, AccessTime, Info, Warning, LocationOn,
} from '@edx/paragon/icons';
import { FormattedMessage, defineMessages } from 'gatsby-plugin-react-intl';

import { intlShape } from '@prospectus/common/types/intlShape';
import ProspectusLink from '@prospectus/common/ui-strict/ProspectusLink';
import {
  hasStarted,
  getHoursOfEffort,
} from '@utils/course';
import {
  formatEffort,
  getI18nDateString,
} from '@utils/formatting';

const messages = defineMessages({
  'prospectus.program_pathway_course.tooltip_label': {
    id: 'prospectus.program_pathway_course.tooltip_label',
    defaultMessage: 'More information',
    description: 'Screenreader label describing the reason for a clickable tooltip',
  },
});

const overrideCollapsibleTitle = (courseTitle, courseUuid, intl) => {
  /*
    Used to provide a tooltip to explain a non-traditional MIT course.
    Implementation ticket: MICROBA-562,
    Fix ticket: MICROBA-576.
    If this isn't fixed by the end of FY21, blame my product manager.
  */
  if (courseUuid !== '48708e7e-0152-4a4b-ad4c-2dadce29c87f') {
    return courseTitle;
  }

  // These is course related text, which will eventually be sourced from discovery. Since
  // it is course text and not general text, we will *not* be translating it
  const MIT_SDS_ASSESSMENT_COURSE_HEADER = 'Have you already taken Data Analysis for Social Scientists?';
  const MIT_SDS_ASSESSMENT_COURSE_EXPLANATION_PRE = 'You must first complete';
  const MIT_SDS_ASSESSMENT_COURSE_LINK_TEXT = '14.310x - Data Analysis for Social Scientists';
  const MIT_SDS_ASSESSMENT_COURSE_EXPLANATION_POST = 'with a passing grade in order to take this assessment and apply it to the MITx Statistics and Data Science MicroMasters program.';

  return (
    <div className="d-flex justify-content-between">
      <span>{courseTitle}</span>
      <OverlayTrigger
        key="top"
        placement="top"
        trigger={['click']}
        rootClose
        overlay={(
          <Tooltip id="tooltip-top" onClick={(e) => e.stopPropagation()}>
            <div style={{ maxWidth: '300px' }} className="border border-dark p-3 mb-3">
              <div>
                <strong>{MIT_SDS_ASSESSMENT_COURSE_HEADER}</strong>
              </div>
              <div>
                {MIT_SDS_ASSESSMENT_COURSE_EXPLANATION_PRE}&nbsp;
                <ProspectusLink
                  href="course/data-analysis-for-social-scientists"
                  className="link"
                  analytics={{
                    eventName: 'edx.bi.user.program-pathway-tooltip.click',
                  }}
                >
                  {MIT_SDS_ASSESSMENT_COURSE_LINK_TEXT}
                </ProspectusLink>&nbsp;
                {MIT_SDS_ASSESSMENT_COURSE_EXPLANATION_POST}
              </div>
            </div>
          </Tooltip>
        )}
      >
        {({ ref, onClick }) => (
          <div
            role="button"
            tabIndex="0"
            aria-label={intl.formatMessage(messages['prospectus.program_pathway_course.tooltip_label'])}
            ref={ref}
            onClick={(e) => { e.stopPropagation(); onClick(e); }}
            onKeyUp={(e) => { e.preventDefault(); e.stopPropagation(); if (e.key === ' ') { onClick(e); } }}
            className="p-1 mr-1 ml-3 d-inline-block"
          >
            <Info />
          </div>
        )}
      </OverlayTrigger>
    </div>
  );
};

function ProgramPathwayCourse(props) {
  const {
    courseTitle,
    courseIsActive,
    index,
    lastStep,
    courseLength,
    courseUuid,
    activeCourse,
    handleTogglePathway,
    courseRun,
    intl,
    subtitle,
    prospectusPath,
    isBundled,
  } = props;

  // See overrideCollapsibleTitle docstring for explanation
  const collapsibleTitle = overrideCollapsibleTitle(courseTitle, courseUuid, intl);

  return (
    <li
      key={courseTitle}
      className={classNames(
        'step',
        {
          'stand-out': courseIsActive,
          first: index === 0,
          last: lastStep === 'courses' && index + 1 === courseLength,
        },
      )}
    >
      <div className="path">
        <div className={classNames('marker', 'course', {
          first: index === 0,
          last: index + 1 === courseLength,
        })}
        > <LibraryBooks className="path-icon" />
        </div>
      </div>
      <div className="path-details">
        {courseUuid !== activeCourse
          && (
          <Collapsible
            className="pl-5.5 py-3.5 pb-3.5"
            title={collapsibleTitle}
            onToggle={isOpen => handleTogglePathway(isOpen, courseTitle, courseUuid)}
            data-testid="course-path-collapsible"
          >
            {courseRun && courseRun.pacingType === 'instructor_paced'
              && (
              <div className="font-weight-bold my-2">
                <CalendarMonth
                  className="stat-icon mr-1"
                />
                <FormattedMessage
                  id="prospectus.program_pathway.start_date"
                  description="Give the start date, past or future"
                  defaultMessage={`
                    {tense, select,
                    future {Starts {startDate}}
                    other {Started {startDate}}}
                  `}
                  values={{
                    startDate: getI18nDateString(courseRun.start, intl),
                    tense: hasStarted(courseRun.start) ? 'past' : 'future',
                  }}
                />
              </div>
              )}
            {courseRun
             && courseRun.minEffort
             && courseRun.maxEffort
             && courseRun.weeksToComplete
             && (
             <div className="font-weight-bold my-2">
               <AccessTime className="stat-icon mr-1" />
               {formatEffort(
                 getHoursOfEffort(courseRun, intl),
                 courseRun.weeksToComplete,
                 intl,
               )}
             </div>
             )}
            <div className="font-weight-normal">
              {!courseRun
                && (
                <div className="mb-2">
                  <Warning className="mr-2" />
                  <span className="font-weight-bold">Not currently available</span>
                </div>
                )}
              {/* eslint-disable-next-line */}
              <div className="mb-3" dangerouslySetInnerHTML={{ __html: subtitle }} />
              {courseRun
                && (
                <div className="mt-2">
                  <ProspectusLink
                    href={prospectusPath}
                    className={classNames({ 'btn-outline': !isBundled, 'inline-link': isBundled })}
                    analytics={{
                      eventName: 'edx.bi.link.program_pathway.course_link.click',
                      linkProps: {
                        location: 'program-pathway',
                      },
                    }}
                  >
                    <FormattedMessage
                      id="prospectus.program_pathway.view_course_link"
                      description="Link to the course details page"
                      defaultMessage="View the course"
                    />
                  </ProspectusLink>
                </div>
                )}
            </div>
          </Collapsible>
          )}
        {courseUuid === activeCourse
          && (
            <div className="collapsible">
              <div className="btn btn-block text-left btn-collapsible active-course">
                <div className="collapsible-title d-flex justify-content-between">
                  <span>{courseTitle}</span>
                  <LocationOn className="pin" />
                </div>
              </div>
            </div>
          )}
      </div>
    </li>
  );
}

ProgramPathwayCourse.propTypes = {
  courseTitle: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  courseUuid: PropTypes.string.isRequired,
  prospectusPath: PropTypes.string.isRequired,
  courseRun: PropTypes.shape({
    key: PropTypes.string.isRequired,
    uuid: PropTypes.string.isRequired,
    marketingPath: PropTypes.string.isRequired,
    minEffort: PropTypes.number.isRequired,
    maxEffort: PropTypes.number.isRequired,
    pacingType: PropTypes.string,
    start: PropTypes.string,
    weeksToComplete: PropTypes.number.isRequired,
  }).isRequired,
  courseIsActive: PropTypes.bool.isRequired,
  lastStep: PropTypes.string,
  courseLength: PropTypes.number,
  activeCourse: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  intl: intlShape.isRequired,
  isBundled: PropTypes.bool.isRequired,
  handleTogglePathway: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

ProgramPathwayCourse.defaultProps = {
  activeCourse: false,
  lastStep: 'courses',
  courseLength: 0,
};

export default track({
  component: 'Course-within-program-pathway',
})(ProgramPathwayCourse);
